"use client";
import React from "react";

import { useAuthStore } from "stores";
import { isFunction, get } from "utils/lodash";
import { useRouter } from "next/navigation";

export interface TypeRedirectProps {
  children: any;
  to?: string;
  callback?: () => void;
}

export interface TypeUnAuthProps {
  children: any;
  to?: string;
  params?: object;
  options?: object;
}

export const AuthRedirect: React.FC<TypeRedirectProps> = ({
  to = "/",
  children,
  callback,
}) => {
  const router = useRouter();
  const { isAuthenticated } = useAuthStore();

  if (isFunction(callback)) return callback();
  React.useEffect(() => {
    if (isAuthenticated) {
      router.push(to);
    }
  }, [isAuthenticated, router, to]);

  if (!isAuthenticated) {
    return children;
  }
  return null;
};

export const UnAuthRedirect: React.FC<TypeUnAuthProps> = ({
  children,
  to = "/login",
  params, // thông tin của page trước khi qua trang login.
}) => {
  const router = useRouter();
  const { isAuthenticated, isLoading, token } = useAuthStore();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    console.log('[UnAuthRedirect] Component mounted');
  }, []);

  React.useEffect(() => {
    console.log('[UnAuthRedirect] Auth state changed:', {
      mounted,
      isLoading,
      isAuthenticated,
      hasToken: !!token,
    });

    // Only redirect after component is mounted and token loading is complete
    if (mounted && !isLoading && !isAuthenticated) {
      console.log('[UnAuthRedirect] Redirecting to login - not authenticated');
      router.push(to);
    }
  }, [isAuthenticated, isLoading, mounted, router, to, token]);

  // Listen for token changes - redirect immediately if token is cleared
  React.useEffect(() => {
    console.log('[UnAuthRedirect] Token changed:', {
      mounted,
      hasToken: !!token,
      isLoading,
    });

    if (mounted && !token && !isLoading) {
      console.log('[UnAuthRedirect] Redirecting to login - no token');
      router.push(to);
    }
  }, [token, mounted, isLoading, router, to]);

  // Show loading or nothing while checking authentication or mounting
  if (!mounted || isLoading) {
    console.log('[UnAuthRedirect] Showing nothing - mounting or loading');
    return null;
  }

  if (isAuthenticated) {
    console.log('[UnAuthRedirect] Rendering children - authenticated');
    return children;
  }

  console.log('[UnAuthRedirect] Returning null - not authenticated');
  return null; // Explicit return when not authenticated
};

export const UnVerifiedRedirect: React.FC<TypeUnAuthProps> = ({ children }) => {
  const router = useRouter();
  const { user } = useAuthStore();
  if (!user || !!get(user, "verifiedEmail")) {
    return children;
  }
  const href = `/otp/${get(user, "email", "")}`;

  router.push(href);
  return null;
};
